import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Input,
  Button,
  VStack,
  Text,
  Avatar,
  IconButton,
  useColorModeValue,
  Divider,
  Textarea,
  Heading,
  Code,
  Spinner,
} from "@chakra-ui/react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import QueryListingPage from "./QueryListingPage";
import SyntheticDataChat from "./SyntheticDataChat";

interface Message {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date;
}
interface chatInterfaceProps {
  onClose: () => void;
}

interface QueryExecutionResponse {
  query: string | null;
  message: string;
  sessionId: string | null;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const ChatInterface: React.FC<chatInterfaceProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hello! How can I help you today?",
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  const [SessionCode, setSessionCode] = useState("string");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [outputText, setOutputText] = useState("// Output will appear here");
  const [queryText, setQueryText] = useState("// Query will appear here");
  const [executionResult, setExecutionResult] = useState(
    "// Execution result will appear here"
  );
  const [showQueryListingPage, setShowQueryListingPage] = useState(false);
  const [showSyntheticDataPage, setshowSyntheticDataPage] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<"json" | "table">("json");

  const bgColor = useColorModeValue("white", "gray.800");
  const userBubbleColor = useColorModeValue("blue.500", "blue.400");
  const aiBubbleColor = useColorModeValue("gray.100", "gray.700");
  const outputBgColor = useColorModeValue("gray.50", "gray.900");

  const [data, setData] = useState([]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    // Simulate AI response (replace with actual API call)
    console.log("Sending message request for:", messages);
    const apiUrl = `${Base_apiUrl}Chat/chat`;

    console.log("Sending request to:", apiUrl);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          userId: 1,
          sessionCode: SessionCode,
          message: userMessage.text,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.table(data);
      setOutputText(data.messaage || "No message received");
      setQueryText(data.query || "No query generated");
      setSessionCode(data.sessionId || "No session id received");
      setData(data);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.messaage || `Generated Query : "${data.query}"`,
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      setOutputText(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `Sorry, there was an error processing your request: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeQuery = async () => {
    if (!queryText || queryText === "// Query will appear here") {
      return;
    }

    setIsExecuting(true);

    try {
      const encodedQuery = encodeURIComponent(queryText);
      const response = await fetch(
        `${Base_apiUrl}Chat/ExecuteGeneratedQuery?GeneratedQuery=${encodedQuery}`,
        {
          headers: {
            accept: "*/*",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: QueryExecutionResponse = await response.json();

      // Format JSON if the message is JSON
      let formattedMessage = result.message;
      try {
        if (result.message.startsWith("[") || result.message.startsWith("{")) {
          const jsonObj = JSON.parse(result.message);
          formattedMessage = JSON.stringify(jsonObj, null, 2);
        }
      } catch (e) {
        // If parsing fails, use the original message
        console.log("Not valid JSON, using original message");
      }

      setExecutionResult(formattedMessage);
    } catch (error) {
      console.error("Error executing query:", error);
      setExecutionResult(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsExecuting(false);
    }
  };
  const renderResultTable = (data: any) => {
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    const rows = Array.isArray(parsed) ? parsed : [parsed];

    if (!rows.length) return <Text>No data to display.</Text>;

    const headers = Object.keys(rows[0]);

    return (
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            {headers.map((header) => (
              <Th key={header}>{header}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row: any, rowIndex: number) => (
            <Tr key={rowIndex}>
              {headers.map((header) => {
                const cellValue = row[header];

                // Display "-" for null, undefined, or objects/arrays
                const displayValue =
                  cellValue === null ||
                  cellValue === undefined ||
                  typeof cellValue === "object"
                    ? "-"
                    : String(cellValue);

                return <Td key={header}>{displayValue}</Td>;
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  } catch (e) {
    return <Text color="red.500">Invalid data for table view</Text>;
  }
};


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (showQueryListingPage) {
    return <QueryListingPage onClose={() => setShowQueryListingPage(false)} />;
  }
  if (showSyntheticDataPage) {
    return (
      <SyntheticDataChat onClose={() => setshowSyntheticDataPage(false)} />
    );
  }

  return (
    <Flex direction="column" w="100%" maxW="1000px" gap={4}>
      <Box
        borderRadius="lg"
        boxShadow="md"
        bg={bgColor}
        p={4}
        maxH="300px"
        overflowY="auto"
      >
        <Heading size="sm" mb={2}>
          Conversation
        </Heading>
        <VStack spacing={4} align="stretch">
          {messages.map((message) => (
            <Flex
              key={message.id}
              justify={message.sender === "user" ? "flex-end" : "flex-start"}
            >
              <Box
                bg={message.sender === "user" ? userBubbleColor : aiBubbleColor}
                color={message.sender === "user" ? "white" : "black"}
                p={3}
                borderRadius="lg"
                maxW="80%"
              >
                <Text>{message.text}</Text>
                <Text fontSize="xs" opacity={0.8} mt={1}>
                  {message.timestamp.toLocaleTimeString()}
                </Text>
              </Box>
            </Flex>
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>
      <Box borderRadius="lg" boxShadow="md" overflow="hidden" bg={bgColor}>
        <Box p={4}>
          <Flex>
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              resize="none"
              rows={1}
              mr={2}
              borderRadius="md"
            />
            <IconButton
              colorScheme="blue"
              aria-label="Send message"
              icon={<ArrowUpIcon />}
              onClick={handleSendMessage}
              isLoading={isLoading}
              alignSelf="flex-end"
            />
          </Flex>
        </Box>
      </Box>

      {/* Query Box */}
      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Heading size="sm">Query</Heading>
          <Button
            colorScheme="green"
            size="sm"
            onClick={executeQuery}
            isLoading={isExecuting}
            loadingText="Executing..."
          >
            Execute Query
          </Button>
        </Flex>
        <Code
          display="block"
          whiteSpace="pre"
          p={2}
          borderRadius="md"
          fontSize="sm"
          width="100%"
          overflowX="auto"
        >
          {queryText}
        </Code>
      </Box>

      {/* Execution Result Box */}
      <Box>
        <Heading size="sm" mb={2}>
          Execution Result
        </Heading>

        <Flex mb={2} gap={2}>
          <Button
            size="xs"
            colorScheme={viewMode === "json" ? "blue" : "gray"}
            onClick={() => setViewMode("json")}
          >
            JSON
          </Button>
          <Button
            size="xs"
            colorScheme={viewMode === "table" ? "blue" : "gray"}
            onClick={() => setViewMode("table")}
          >
            Table
          </Button>

          <Button
            size="xs"
            colorScheme="blue"
            ml="auto"
            onClick={() => {
              if (!executionResult) return;

              try {
                const jsonData =
                  typeof executionResult === "string"
                    ? JSON.parse(executionResult)
                    : executionResult;
                const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `query-result-${Date.now()}.json`;
                link.click();
                URL.revokeObjectURL(url);
              } catch (error) {
                alert("Could not download as JSON.");
              }
            }}
            isDisabled={!executionResult}
          >
            Download JSON
          </Button>
          <Button
            size="xs"
            colorScheme="green"
            onClick={() => {
              try {
                let jsonData =
                  typeof executionResult === "string"
                    ? JSON.parse(executionResult)
                    : executionResult;

                if (!Array.isArray(jsonData)) {
                  if (typeof jsonData === "object") {
                    jsonData = [jsonData];
                  } else {
                    throw new Error("Invalid data for CSV.");
                  }
                }

                const headers = Object.keys(jsonData[0]);
                let csvContent = headers.join(",") + "\n";
                jsonData.forEach((row: any) => {
                  const line = headers
                    .map((field) => {
                      const val = row[field];
                      const str =
                        val === null || val === undefined ? "" : String(val);
                      return /[,"\n]/.test(str)
                        ? `"${str.replace(/"/g, '""')}"`
                        : str;
                    })
                    .join(",");
                  csvContent += line + "\n";
                });

                const blob = new Blob([csvContent], {
                  type: "text/csv;charset=utf-8;",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `query-result-${Date.now()}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              } catch (error) {
                alert("Could not download as CSV.");
              }
            }}
            isDisabled={!executionResult}
          >
            Download CSV
          </Button>
        </Flex>

        {isExecuting ? (
          <Spinner size="sm" />
        ) : viewMode === "json" ? (
          <Code
            display="block"
            whiteSpace="pre"
            p={2}
            borderRadius="md"
            fontSize="sm"
            width="100%"
            overflowY="auto"
            maxH="300px"
          >
            {typeof executionResult === "string"
              ? executionResult
              : JSON.stringify(executionResult, null, 2)}
          </Code>
        ) : (
          <Box overflowX="auto" border="1px" borderRadius="md">
            {renderResultTable(executionResult)}
          </Box>
        )}
      </Box>

      <Text fontSize="sm" color="gray.600" textAlign="center">
        <Button
          variant="link"
          colorScheme="blue"
          onClick={() => setShowQueryListingPage(true)}
        >
          Click here to view saved queries
        </Button>
      </Text>
      <hr />
      <Text fontSize="sm" color="gray.600" textAlign="center">
        <Button
          variant="link"
          colorScheme="blue"
          onClick={() => setshowSyntheticDataPage(true)}
        >
          Click here to Generate Synthetic Data.
        </Button>
      </Text>
    </Flex>
  );
};

export default ChatInterface;
